import { Global, Theme, css } from '@emotion/react';

import { Platform } from '../constants';
import { AppTypography } from './appDesignTokens';

type Direction =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'all'
  | 'horizontal'
  | 'vertical';

type SingleDirection = Extract<Direction, 'top' | 'bottom' | 'left' | 'right'>;

const _directions: { [key in Direction]: SingleDirection[] } = {
  top: ['top'],
  bottom: ['bottom'],
  left: ['left'],
  right: ['right'],
  all: ['top', 'bottom', 'left', 'right'],
  horizontal: ['left', 'right'],
  vertical: ['top', 'bottom'],
};

export const safeArea = (
  direction: SingleDirection,
  type: 'padding' | 'margin' = 'padding',
  min = 0,
  add = 0
) =>
  direction === 'top'
    ? css`
        ${type === 'padding' &&
        css`
          padding-top: max(
            calc(constant(safe-area-inset-top) + ${add}px),
            ${min}px
          );
          padding-top: max(calc(env(safe-area-inset-top) + ${add}px), ${min}px);
        `}
        ${type === 'margin' &&
        css`
          margin-top: max(
            calc(constant(safe-area-inset-top) + ${add}px),
            ${min}px
          );
          margin-top: max(calc(env(safe-area-inset-top) + ${add}px), ${min}px);
        `}
      `
    : direction === 'bottom'
    ? css`
        ${type === 'padding' &&
        css`
          padding-bottom: max(
            calc(constant(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
          padding-bottom: max(
            calc(env(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
        `}
        ${type === 'margin' &&
        css`
          margin-bottom: max(
            calc(constant(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
          margin-bottom: max(
            calc(env(safe-area-inset-bottom) + ${add}px),
            ${min}px
          );
        `}
      `
    : direction === 'left'
    ? css`
        ${type === 'padding' &&
        css`
          padding-left: max(
            calc(constant(safe-area-inset-left) + ${add}px),
            ${min}px
          );
          padding-left: max(
            calc(env(safe-area-inset-left) + ${add}px),
            ${min}px
          );
        `}
        ${type === 'margin' &&
        css`
          margin-left: max(
            calc(constant(safe-area-inset-left) + ${add}px),
            ${min}px
          );
          margin-left: max(
            calc(env(safe-area-inset-left) + ${add}px),
            ${min}px
          );
        `}
      `
    : direction === 'right'
    ? css`
        ${type === 'padding' &&
        css`
          padding-right: max(
            calc(constant(safe-area-inset-right) + ${add}px),
            ${min}px
          );
          padding-right: max(
            calc(env(safe-area-inset-right) + ${add}px),
            ${min}px
          );
        `}
        ${type === 'margin' &&
        css`
          margin-right: max(
            calc(constant(safe-area-inset-right) + ${add}px),
            ${min}px
          );
          margin-right: max(
            calc(env(safe-area-inset-right) + ${add}px),
            ${min}px
          );
        `}
      `
    : undefined;

export const sticky = (top: number) => css`
  position: sticky;
  top: calc(constant(safe-area-inset-top) + ${top}px);
  top: calc(env(safe-area-inset-top) + ${top}px);
`;

export const multilineEllipsis = (line: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${line};
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const flashAnimation = css`
  @keyframes flash {
    0% {
      opacity: 1;
    }

    50% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;

export const linearGradient = (
  direction: SingleDirection,
  from = 'rgba(87, 87, 87, 0)',
  to = 'rgba(0, 0, 0, 0.5)'
) => css`
  background-image: linear-gradient(to ${direction}, ${from}, ${to});
`;

export const marginXTop = (margin: number) => css`
  margin-top: ${margin}px;
`;

export const marginXBottom = (margin: number) => css`
  margin-bottom: ${margin}px;
`;

export const marginXLeft = (margin: number) => css`
  margin-left: ${margin}px;
`;

export const marginXRight = (margin: number) => css`
  margin-right: ${margin}px;
`;

const _marginXMap = {
  top: marginXTop,
  bottom: marginXBottom,
  left: marginXLeft,
  right: marginXRight,
};

export const marginX = (margin: number, direction: Direction) => {
  return _directions[direction].map((d) => _marginXMap[d](margin));
};

export const fontSize = (size: number) => css`
  font-size: ${size}px;
`;

export const shadow = () => css`
  box-shadow: 0px 1px 5px 2px rgba(128, 128, 128, 0.2);
`;

export const textColor = (color: string) =>
  css`
    color: ${color};
  `;

export const MAX_WIDTH = 500;
export const HEADER_HEIGHT = 44;
export const MODAL_HEADER_HEIGHT = 48;

export const BREAKPOINTS = {
  mobile: 500,
  tablet: 768,
  desktop: 1080,
} as const;

export const media = {
  tablet: `@media (min-width: ${BREAKPOINTS.tablet}px)`,
  desktop: `@media (min-width: ${BREAKPOINTS.desktop}px)`,
} as const;

/** 반응형 max-width: 500 → 768 → 1080 */
export const responsiveMaxWidth = css`
  max-width: ${MAX_WIDTH}px;
  ${media.tablet} {
    max-width: ${BREAKPOINTS.tablet}px;
  }
  ${media.desktop} {
    max-width: ${BREAKPOINTS.desktop}px;
  }

  .has-sidebar & {
    ${media.tablet} {
      max-width: none;
      left: var(--sidebar-width, 0px);
      right: 0;
      width: auto;
    }
  }
`;

/** 반응형 horizontal padding: 16 → 24 → 32 */
export const responsivePadding = css`
  padding-left: 16px;
  padding-right: 16px;
  ${media.tablet} {
    padding-left: 24px;
    padding-right: 24px;
  }
  ${media.desktop} {
    padding-left: 32px;
    padding-right: 32px;
  }
`;

export const GlobalStyle = ({ platform }: { platform: Platform }) => (
  <Global
    styles={(theme: Theme) => css`
      :root, html[data-theme] {
        --theme-white: ${theme.var.white};
        --theme-black: ${theme.var.black};
        --theme-gray50: ${theme.var.gray50};
        --theme-gray100: ${theme.var.gray100};
        --theme-gray200: ${theme.var.gray200};
        --theme-gray300: ${theme.var.gray300};
        --theme-gray400: ${theme.var.gray400};
        --theme-gray500: ${theme.var.gray500};
        --theme-gray600: ${theme.var.gray600};
        --theme-gray700: ${theme.var.gray700};
        --theme-gray800: ${theme.var.gray800};
        --theme-gray900: ${theme.var.gray900};
        --theme-overlay-bg: ${theme.overlay.bg};
        --theme-border-light: ${theme.border.light};
        --theme-border-lightest: ${theme.border.lightest};
        --theme-main500: ${theme.colors.main500};
        --theme-main100: ${theme.colors.main100};
        --theme-main50: ${theme.colors.main50};
        --theme-main200: ${theme.colors.main200};
        --theme-play-gra: ${theme.colors.playGra};
      }

      html,
      body {
        background-color: ${theme.var.white};
        color: ${theme.var.black};
        transition: background-color 0.2s ease, color 0.2s ease;
      }

      ${flashAnimation}

      ${platform === Platform.ios &&
      `
          .imp-payment-progress {
    top: 50px !important;
  }
`}
      h1 {
        ${AppTypography.h1}
      }

      h2 {
        ${AppTypography.h2}
      }

      h3 {
        ${AppTypography.h3}
      }

      h4 {
        ${AppTypography.h4}
      }

      h5 {
        ${AppTypography.h5}
      }

      h6 {
        ${AppTypography.h6}
      }
      .cv-fullscreen-modal__content {
        .reactEasyCrop_CropArea::before {
          content: '';
          position: absolute;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 34% !important;
          background: rgba(0, 0, 0, 0.5) !important;
          border: none !important;
        }

        .reactEasyCrop_CropArea::after {
          border: none !important;
        }
      }

      .react-calendar__tile:disabled > abbr {
        color: ${theme.var.gray400} !important;
      }

      .react-calendar__navigation button:enabled:hover,
      .react-calendar__navigation button:enabled:focus {
        background-color: ${theme.var.white} !important;
      }

      .react-calendar__tile--highlight-latest {
        color: ${theme.true.white} !important;
      }

      .cv-editor {
        background-color: ${theme.var.gray50};
      }

      ${platform === Platform.web &&
      css`
        #overlay {
          max-width: ${MAX_WIDTH}px;
          margin: 0 auto;
          ${media.tablet} { max-width: ${BREAKPOINTS.tablet}px; }
          ${media.desktop} { max-width: ${BREAKPOINTS.desktop}px; }
        }

        .cv-writer {
          max-width: ${MAX_WIDTH}px;
          ${media.tablet} { max-width: ${BREAKPOINTS.tablet}px; }
          ${media.desktop} { max-width: ${BREAKPOINTS.desktop}px; }
        }
      `}
    `}
  />
);
